import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Globe, Loader2, Copy, Save, ShieldCheck, ExternalLink, Monitor, Tablet, Smartphone } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SensitiveOtpGate from "@/components/SensitiveOtpGate";

export default function PublicPageSettings() {
  return (
    <SensitiveOtpGate
      title="Verifica accesso Area Pubblica"
      description="Per modificare la pagina pubblica dell'agenzia (titolo, colori, logo, embed), conferma la tua identità con un codice inviato all'email dell'amministratore."
    >
      <PublicPageSettingsForm />
    </SensitiveOtpGate>
  );
}

function PublicPageSettingsForm() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    public_page_title: "",
    public_page_subtitle: "",
    public_page_intro: "",
    public_page_primary_color: "#2563EB",
    public_page_logo_url: "",
    public_page_cta_text: "",
    public_page_enabled_quote_types: [],
  });
  const QUOTE_TYPES = [
    { v: "auto", l: "Auto" },
    { v: "casa", l: "Casa" },
    { v: "vita", l: "Vita" },
    { v: "salute", l: "Salute" },
    { v: "infortuni", l: "Infortuni" },
    { v: "viaggio", l: "Viaggio" },
    { v: "responsabilita_civile", l: "Responsabilità civile" },
  ];

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/agency/public-page-settings");
        setData(d);
        setForm({
          public_page_title: d.public_page_title || "",
          public_page_subtitle: d.public_page_subtitle || "",
          public_page_intro: d.public_page_intro || "",
          public_page_primary_color: d.public_page_primary_color || "#2563EB",
          public_page_logo_url: d.public_page_logo_url || "",
          public_page_cta_text: d.public_page_cta_text || "",
          public_page_enabled_quote_types: d.public_page_enabled_quote_types || [],
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore caricamento");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/agency/public-page-settings", form);
      setData(d);
      toast.success("Personalizzazione salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = () => {
    if (!data?.public_url) return;
    navigator.clipboard.writeText(data.public_url);
    toast.success("Link copiato");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="public-page-settings">
      <header>
        <p className="text-xs uppercase tracking-wider text-slate-500">Personalizzazione</p>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Globe className="h-6 w-6 text-blue-600" /> Area pubblica preventivi
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Personalizza il modulo pubblico dove i tuoi clienti potranno richiederti un preventivo.
        </p>
      </header>

      {/* URL pubblica */}
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50">
        <CardContent className="py-4">
          <Label className="text-[11px] uppercase tracking-wider text-blue-700 font-semibold">URL pubblico della tua agenzia</Label>
          <div className="flex items-center gap-2 mt-2">
            <code className="flex-1 bg-white border border-blue-200 rounded px-3 py-2 text-sm font-mono text-blue-900 break-all" data-testid="public-url-display">
              {data?.public_url || "(APP_PUBLIC_URL non configurato)"}
            </code>
            <Button onClick={copyUrl} disabled={!data?.public_url} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="copy-public-url">
              <Copy className="h-4 w-4 mr-1.5" /> Copia
            </Button>
            {data?.public_url && (
              <Button asChild variant="outline" className="border-blue-300">
                <a href={data.public_url} target="_blank" rel="noopener noreferrer" data-testid="open-public-url">
                  <ExternalLink className="h-4 w-4 mr-1.5" /> Apri
                </a>
              </Button>
            )}
          </div>
          <p className="text-[10px] text-blue-600 mt-2">
            Condividi questo link sui social, nelle email, sul sito web. Tutte le richieste arriveranno solo alla tua agenzia.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Personalizzazione</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs font-semibold">Titolo principale</Label>
            <Input
              data-testid="pp-title"
              value={form.public_page_title}
              onChange={(e) => setForm({ ...form, public_page_title: e.target.value })}
              placeholder={`Es: Richiedi un preventivo a ${data?.agency_name || "..."}`}
              maxLength={120}
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Sottotitolo / payoff</Label>
            <Input
              data-testid="pp-subtitle"
              value={form.public_page_subtitle}
              onChange={(e) => setForm({ ...form, public_page_subtitle: e.target.value })}
              placeholder="Es: Compila il modulo, ti risponderemo entro 24h con la migliore proposta personalizzata."
              maxLength={300}
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Introduzione aggiuntiva (opzionale)</Label>
            <Textarea
              data-testid="pp-intro"
              value={form.public_page_intro}
              onChange={(e) => setForm({ ...form, public_page_intro: e.target.value })}
              placeholder="Es: Offriamo soluzioni assicurative per famiglie, professionisti e PMI dal 1985."
              rows={3}
              maxLength={300}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Colore primario</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  data-testid="pp-color"
                  type="color"
                  value={form.public_page_primary_color}
                  onChange={(e) => setForm({ ...form, public_page_primary_color: e.target.value })}
                  className="h-10 w-14 rounded border"
                />
                <Input
                  value={form.public_page_primary_color}
                  onChange={(e) => setForm({ ...form, public_page_primary_color: e.target.value })}
                  className="font-mono"
                  maxLength={20}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Testo bottone CTA</Label>
              <Input
                data-testid="pp-cta"
                value={form.public_page_cta_text}
                onChange={(e) => setForm({ ...form, public_page_cta_text: e.target.value })}
                placeholder="Richiedi preventivo"
                maxLength={50}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold">Logo dell&apos;agenzia</Label>
            <div className="flex items-center gap-3 mt-1.5">
              {form.public_page_logo_url ? (
                <img src={form.public_page_logo_url} alt="logo" className="h-16 w-16 rounded border border-slate-200 object-cover" />
              ) : (
                <div className="h-16 w-16 rounded border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-[10px] text-slate-400">
                  no logo
                </div>
              )}
              <div className="flex-1 space-y-1.5">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  data-testid="pp-logo-upload"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 5 * 1024 * 1024) { toast.error("Logo troppo grande (max 5 MB)"); return; }
                    setBusy(true);
                    try {
                      const fd = new FormData();
                      fd.append("file", f);
                      const { data: r } = await api.post("/agency/public-page-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setForm((prev) => ({ ...prev, public_page_logo_url: r.logo_url }));
                      toast.success("Logo caricato");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Errore upload");
                    } finally {
                      setBusy(false);
                      e.target.value = "";
                    }
                  }}
                  className="text-xs"
                  disabled={busy}
                />
                {form.public_page_logo_url && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm("Rimuovere il logo personalizzato?")) return;
                      setBusy(true);
                      try {
                        await api.delete("/agency/public-page-logo");
                        setForm((prev) => ({ ...prev, public_page_logo_url: "" }));
                        toast.success("Logo rimosso");
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Errore");
                      } finally { setBusy(false); }
                    }}
                    className="text-[10px] text-rose-600 hover:underline"
                    data-testid="pp-logo-remove"
                  >
                    Rimuovi logo
                  </button>
                )}
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">PNG/JPG/WEBP/SVG · max 5 MB · ideale 200×200px quadrato.</p>
            <details className="mt-1 text-[10px] text-slate-400">
              <summary className="cursor-pointer">Usa URL esterno invece di caricare</summary>
              <Input
                data-testid="pp-logo-url"
                value={form.public_page_logo_url}
                onChange={(e) => setForm({ ...form, public_page_logo_url: e.target.value })}
                placeholder="https://…"
                className="mt-1 text-xs"
              />
            </details>
          </div>

          {/* Tipologie preventivo abilitate */}
          <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3">
            <Label className="text-xs font-semibold">Tipologie preventivo abilitate sull&apos;area pubblica</Label>
            <p className="text-[11px] text-slate-600 mt-0.5 mb-2">
              Spunta solo le tipologie su cui vuoi ricevere richieste. Se non spunti nulla, sono <b>tutte attive</b>.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {QUOTE_TYPES.map((qt) => {
                const checked = (form.public_page_enabled_quote_types || []).includes(qt.v);
                return (
                  <label key={qt.v} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded border cursor-pointer ${checked ? "border-amber-400 bg-amber-100" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                    <input
                      type="checkbox"
                      data-testid={`pp-quote-type-${qt.v}`}
                      checked={checked}
                      onChange={() => {
                        const cur = form.public_page_enabled_quote_types || [];
                        const next = checked ? cur.filter((x) => x !== qt.v) : [...cur, qt.v];
                        setForm({ ...form, public_page_enabled_quote_types: next });
                      }}
                      className="h-3.5 w-3.5"
                    />
                    {qt.l}
                  </label>
                );
              })}
            </div>
          </div>


          {/* Anteprima */}
          <div className="rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Anteprima header</p>
            <div className="bg-white rounded border border-slate-200 p-3 flex items-center gap-3">
              {form.public_page_logo_url ? (
                <img src={form.public_page_logo_url} alt="logo" className="h-10 w-10 rounded object-cover" />
              ) : (
                <div className="h-10 w-10 rounded flex items-center justify-center" style={{ background: form.public_page_primary_color || "#2563EB" }}>
                  <ShieldCheck className="h-5 w-5 text-white" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: form.public_page_primary_color || "#2563EB" }}>
                  {data?.agency_name}
                </p>
                <h2 className="font-semibold text-slate-900 truncate">{form.public_page_title || `Richiedi un preventivo a ${data?.agency_name}`}</h2>
                {form.public_page_subtitle && <p className="text-xs text-slate-600 mt-0.5 truncate">{form.public_page_subtitle}</p>}
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="pp-save">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Salva personalizzazione
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* API Key + Embed Code */}
      <ApiKeySection />
    </div>
  );
}

function ApiKeySection() {
  const [apiData, setApiData] = useState(null);
  const [loadingApi, setLoadingApi] = useState(true);
  const [regen, setRegen] = useState(false);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/agency/api-key");
        setApiData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore caricamento API key");
      } finally { setLoadingApi(false); }
    })();
  }, []);

  const regenerate = async () => {
    if (!window.confirm("⚠️ Rigenerare la API key invaliderà tutte le integrazioni esistenti sui tuoi siti. Confermi?")) return;
    setRegen(true);
    try {
      const { data } = await api.post("/agency/api-key/regenerate");
      const { data: full } = await api.get("/agency/api-key");
      setApiData(full);
      toast.success("API key rigenerata");
      setShowKey(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setRegen(false); }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiato`);
  };

  if (loadingApi) return null;
  if (!apiData) return null;

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          🔑 API key &amp; Embed sul tuo sito
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-xs font-semibold">La tua API key</Label>
          <div className="flex items-center gap-2 mt-1">
            <code className="flex-1 bg-white border border-amber-200 rounded px-3 py-2 text-xs font-mono break-all" data-testid="api-key-display">
              {showKey ? apiData.api_key : "ag_••••••••••••••••••••••••••••••••"}
            </code>
            <Button size="sm" variant="outline" onClick={() => setShowKey(!showKey)} data-testid="api-key-toggle">
              {showKey ? "Nascondi" : "Mostra"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => copy(apiData.api_key, "API key")} data-testid="api-key-copy">
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" onClick={regenerate} disabled={regen} className="text-rose-700 border-rose-300" data-testid="api-key-regen">
              {regen ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Rigenera"}
            </Button>
          </div>
          {apiData.created_at && (
            <p className="text-[10px] text-slate-500 mt-1">
              Creata il {new Date(apiData.created_at).toLocaleString("it-IT")}
            </p>
          )}
        </div>

        {apiData.iframe_snippet && (
          <div>
            <Label className="text-xs font-semibold">📋 Codice iframe (WordPress, Wix, sito custom)</Label>
            <p className="text-[11px] text-slate-600 mt-0.5 mb-1">Incolla questo HTML nella tua pagina:</p>
            <div className="relative">
              <pre className="bg-slate-900 text-emerald-300 text-[11px] font-mono p-3 rounded overflow-x-auto whitespace-pre-wrap break-all" data-testid="iframe-snippet">
                {apiData.iframe_snippet}
              </pre>
              <button
                onClick={() => copy(apiData.iframe_snippet, "Snippet iframe")}
                className="absolute top-2 right-2 text-[10px] bg-slate-700 hover:bg-slate-600 text-white px-2 py-1 rounded"
                data-testid="iframe-snippet-copy"
              >
                Copia
              </button>
            </div>
            {apiData.iframe_url && (
              <details className="mt-3" open>
                <summary className="cursor-pointer text-xs font-semibold text-emerald-900">
                  👁️ Anteprima live (come apparirà sul tuo sito)
                </summary>
                <ViewportSelector iframeUrl={apiData.iframe_url} />
              </details>
            )}
          </div>
        )}

        {apiData.api_snippet && (
          <details>
            <summary className="cursor-pointer text-xs font-semibold text-amber-900">
              🛠️ Integrazione via API (avanzato)
            </summary>
            <p className="text-[11px] text-slate-600 mt-1 mb-1">
              Per sviluppatori che vogliono integrare via POST custom (passando l&apos;API key nell&apos;header X-API-Key):
            </p>
            <div className="relative">
              <pre className="bg-slate-900 text-emerald-300 text-[11px] font-mono p-3 rounded overflow-x-auto whitespace-pre-wrap" data-testid="api-snippet">
                {apiData.api_snippet}
              </pre>
              <button
                onClick={() => copy(apiData.api_snippet, "cURL")}
                className="absolute top-2 right-2 text-[10px] bg-slate-700 hover:bg-slate-600 text-white px-2 py-1 rounded"
              >
                Copia
              </button>
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

function ViewportSelector({ iframeUrl }) {
  const viewports = [
    { id: "desktop", label: "Desktop", icon: Monitor, width: "100%" },
    { id: "tablet", label: "Tablet", icon: Tablet, width: "768px" },
    { id: "mobile", label: "Mobile", icon: Smartphone, width: "375px" },
  ];
  const [active, setActive] = useState("desktop");
  const v = viewports.find((x) => x.id === active);
  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-[11px] text-slate-600 font-semibold">Anteprima su:</span>
        <div className="flex bg-slate-100 rounded-md p-0.5 gap-0.5">
          {viewports.map((vp) => {
            const Icon = vp.icon;
            return (
              <button
                key={vp.id}
                type="button"
                onClick={() => setActive(vp.id)}
                data-testid={`preview-viewport-${vp.id}`}
                className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded transition ${
                  active === vp.id ? "bg-white shadow-sm font-semibold text-slate-900" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Icon className="h-3 w-3" /> {vp.label}
              </button>
            );
          })}
        </div>
        <span className="text-[10px] text-slate-400">({v.width === "100%" ? "full width" : v.width})</span>
      </div>
      <div className="rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50/50 p-2 flex justify-center">
        <div style={{ width: v.width, maxWidth: "100%", transition: "width 0.3s" }}>
          <div className="bg-slate-200 rounded-t-md px-3 py-1.5 flex items-center gap-1.5 border-b border-slate-300">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
            <span className="ml-2 text-[10px] text-slate-600 font-mono truncate">{iframeUrl}</span>
          </div>
          <iframe
            src={`${iframeUrl}?embed=1`}
            width="100%"
            height="500"
            frameBorder="0"
            title="Anteprima form preventivi"
            data-testid="iframe-preview"
            style={{ border: "none", borderRadius: "0 0 6px 6px", background: "#fff", display: "block" }}
          />
        </div>
      </div>
      <p className="text-[10px] text-slate-500 mt-1.5 text-center">
        ↑ Questo è esattamente come apparirà sul tuo sito esterno
      </p>
    </div>
  );
}

